/**
 * English — the source language, and the fallback for every other one.
 *
 * Keys are flat and dotted, grouped by where they are said. A message is a
 * string, or an object of plural forms picked by a `count` parameter; `{name}`
 * is a placeholder. See `lib/i18n.js`.
 */
export default {
  'date.today': 'Today',
  'date.yesterday': 'Yesterday',
  'date.tomorrow': 'Tomorrow',
  'date.relative.today': 'today',
  'date.relative.tomorrow': 'tomorrow',
  'date.relative.yesterday': 'yesterday',
  'date.relative.in': {
    one: 'in {count} day',
    other: 'in {count} days',
  },
  'date.relative.ago': {
    one: '{count} day ago',
    other: '{count} days ago',
  },

  'update.ready': 'A new version is ready.',
  'update.detail': 'It may bring your data up to date.',
  'update.now': 'Update now',

  'privacy.off': 'Privacy off — everything visible',
  'privacy.on': 'Privacy on — totals hidden',
  'privacy.strict': 'Privacy strict — every amount hidden',

  'header.console': 'SQL console',
  'header.achievements': 'Achievements',
  'achievements.title': 'Achievements',
  'achievements.subtitle': '{earned} of {total} earned',
  'achievements.since': 'Counting from {date}, the day you started using OKANE. Nothing before it counts.',
  'achievements.counting': 'Counting…',
  'achievements.earnedOn': 'Earned {date}',
  'achievements.unlocked': {
    one: 'Achievement unlocked: {names}',
    other: '{count} achievements unlocked: {names}',
  },
  'achievements.unit.months': { one: '{have} of {count} month', other: '{have} of {count} months in a row' },
  'achievements.unit.days': { one: '{have} of {count} day', other: '{have} of {count} days' },
  'achievements.unit.payments': { one: '{have} of {count} payment', other: '{have} of {count} payments' },
  'achievements.unit.goals': { one: '{have} of {count} goal', other: '{have} of {count} goals' },
  'achievement.saver.title': 'Saver',
  'achievement.saver.desc': 'A month with less spending than the month before.',
  'achievement.superSaver.title': 'Super Saver',
  'achievement.superSaver.desc': 'Three months in a row, each spending less than the last.',
  'achievement.ultraSaver.title': 'Ultra Saver',
  'achievement.ultraSaver.desc': 'Six months in a row, each spending less than the last.',
  'achievement.earner.title': 'Earner',
  'achievement.earner.desc': 'A month with more income than the month before.',
  'achievement.superEarner.title': 'Super Earner',
  'achievement.superEarner.desc': 'Three months in a row, each earning more than the last.',
  'achievement.ultraEarner.title': 'Ultra Earner',
  'achievement.ultraEarner.desc': 'Six months in a row, each earning more than the last.',
  'achievement.inTheBlack.title': 'In the Black',
  'achievement.inTheBlack.desc': 'A month where more came in than went out.',
  'achievement.steadyHand.title': 'Steady Hand',
  'achievement.steadyHand.desc': 'Three months in a row in the black.',
  'achievement.quietWeek.title': 'Quiet Week',
  'achievement.quietWeek.desc': 'Seven days in a row without spending anything.',
  'achievement.perfectPlan.title': 'Perfect Plan',
  'achievement.perfectPlan.desc': '50 planned payments that came as planned.',
  'achievement.firstStep.title': 'First Step',
  'achievement.firstStep.desc': 'Set up your first savings goal.',
  'achievement.goalOriented.title': 'Goal-oriented',
  'achievement.goalOriented.desc': 'Reach your first savings goal.',
  'achievement.overAchiever.title': 'Overachiever',
  'achievement.overAchiever.desc': 'Reach three savings goals.',
  'achievement.accountant.title': 'Accountant',
  'achievement.accountant.desc': '30 days of keeping your books with OKANE.',
  'achievement.ministerOfFinance.title': 'Minister of Finance',
  'achievement.ministerOfFinance.desc': '100 days of keeping your books with OKANE.',
  'header.lightTheme': 'Light theme',
  'header.darkTheme': 'Dark theme',
  'header.menu': 'Menu',
  'header.import': 'Import a statement',
  'header.export': 'Export to CSV',
  'export.date': 'Date',
  'export.description': 'Description',
  'export.amount': 'Amount ({currency})',
  'export.category': 'Category',
  'export.tags': 'Tags',
  'export.paymentType': 'Payment type',
  'export.notes': 'Notes',
  'export.status': 'Status',
  'export.pending': 'pending',
  'export.nothing': 'Nothing to export — no transaction is showing.',
  'export.done': {
    one: 'Exported {count} transaction',
    other: 'Exported {count} transactions',
  },
  'header.settings': 'Settings',
  'header.help': 'Help',

  'filter.currently': '{label}, currently {value}',
  'filter.remove': 'Remove the {label} filter',
  'filter.search': 'Search or filter…',
  'filter.searchShort': 'Search…',
  'filter.searchLabel': 'Search transactions',
  'filter.clearSearch': 'Clear search',
  'filter.add': 'Add a filter',
  'filter.noMatch': 'No filter matches that — it is being searched for instead.',
  'filter.clearAll': 'Clear everything',

  'common.cancel': 'Cancel',
  'common.apply': 'Apply',
  'common.add': 'Add',
  'common.save': 'Save',
  'common.done': 'Done',
  'common.delete': 'Delete',
  'common.editThing': 'Edit {name}',
  'common.sure': 'Sure?',
  'common.exists': '“{name}” already exists.',
  'goals.emptyHint': 'Set an amount and a deadline; OKANE takes the share out of your balance as it goes — no transactions needed.',
  'bulk.tagsHint': 'A ticked tag is already on all of them — pick it to take it off. A dashed one is on some; pick it to give it to the rest, again to take it off everywhere. A name that does not exist yet is made here.',
  'categorySheet.placeholder': 'Groceries, Rent, Salary…',
  'categorySheet.hint': 'The number is how many transactions are filed here. A tag on a category is worn by every one of them without being copied onto anything — take it off the category and they all lose it. Renaming moves the name across all your financial data; deleting only takes the label off, and leaves the transactions.',
  'tagSheet.hint': 'The number is how many transactions wear the tag. Deleting a tag takes it off all of them; the transactions themselves are untouched.',
  'console.runHint': '{keys} runs it.',
  'console.ms': '{ms} ms',
  'import.or': 'or',
  'import.sheetRows': {
    one: '{name} — {count} row',
    other: '{name} — {count} rows',
  },
  'import.skippedN': '{count} skipped',
  'import.ignoredN': '{count} ignored',
  'import.andMore': 'and {count} more.',
  'import.failed': 'Nothing was imported: {reason}',
  'settings.readFailed': 'Could not read that file: {reason}',
  'charts.maskedNote': '{caption}. Figures hidden by privacy mode.',
  'app.attention': {
    one: '{count} thing needs attention',
    other: '{count} things need attention',
  },
  'backup.aesNote': "Encrypted with AES-256. OKANE opens it again under Restore, and so do 7-Zip, Keka, WinZip and p7zip — but not macOS's own Archive Utility or a plain unzip, which have never learned this kind of password.",
  'backup.noWayBack': 'There is no way back into it without the password. Nobody, including OKANE, can recover it for you.',
  'backup.withoutPassword': 'Without a password',
  'backup.backUp': 'Back up',
  'backup.packing': 'Packing…',
  'error.zip.need-password': 'A password is needed to protect the backup.',
  'error.zip.not-zip': 'That is not a zip file.',
  'error.zip.not-ours': 'That zip is not password-protected in the way OKANE writes them.',
  'error.zip.wrong-password': 'Wrong password.',
  'error.zip.damaged': 'That backup has been damaged since it was made.',
  'error.zip.empty': 'There is nothing in that zip.',
  'error.sheet.empty': 'That file is empty.',
  'error.sheet.web-page': 'That is a web page saved as .xls — re-export it as a real Excel file.',
  'error.sheet.not-excel': 'Not an Excel file — export the statement as .xlsx or .xls.',
  'error.sheet.no-workbook': 'This file has no workbook in it.',
  'error.sheet.password': 'This file is password-protected.',
  'error.sheet.no-worksheets': 'This workbook has no worksheets.',
  'error.sheet.cannot-unzip': 'This browser cannot unpack .xlsx files — save the sheet as .xls or .csv.',
  'error.sheet.bad-xlsx': 'Not a readable .xlsx file.',
  'error.sheet.zip-not-sheet': 'This file is a zip, but not a spreadsheet.',
  'error.storage-lost': 'OKANE keeps its data in a file this browser can no longer open. Use the browser it was set up in.',
  'error.offline': 'The OKANE server cannot be reached.',
  'error.disconnected': 'The connection to the server was lost.',
  'error.refused': 'The server refused the connection.',
  'error.denied': 'Your role in this organisation does not allow that.',
  'error.signed-out': 'Your session has ended. Sign in again.',
  'error.bad-email': 'That is not an e-mail address.',
  'error.bad-name': 'An organisation needs a name of up to 100 characters.',
  'error.slow-down': 'Too many attempts. Try again later.',
  'error.mail': 'The e-mail could not be sent. Try again in a moment.',
  'error.link-expired': 'That link has expired or was already used. Ask for a new one.',
  'error.ceremony': 'That took too long. Try again.',
  'error.passkey': 'The passkey was not accepted.',
  'error.expired': 'This code has expired. Get a new one.',
  'error.last-owner': 'An organisation must keep at least one owner. Make someone else an owner first.',
  'error.server': 'Something went wrong on the server.',
  'boot.backingUp': 'Backing up your data…',
  'boot.updating': 'Updating your data',
  'boot.savingCopy': 'Saving a safety copy before the update.',
  'boot.almostThere': 'Almost there…',
  'boot.step': 'Step {n} of {total}',
  'boot.awayWhile': 'You have been away a while — bringing your records up to date. Please keep the app open.',
  'boot.aheadTitle': 'Update OKANE to continue',
  'boot.aheadLead': 'This copy of the app is older than your data (v{app} vs v{data}). Reload to get the latest version — your data is untouched and safe.',
  'boot.reload': 'Reload',
  'boot.errorTitle': "The update didn't finish",
  'boot.errorLead': 'Your data was not changed past the last completed step, and a backup was taken first. You can try again.',
  'boot.errorLeadBackup': 'Your data was not changed past the last completed step, and a backup was taken first. You can try again, or download the backup taken before the update.',
  'boot.tryAgain': 'Try again',
  'boot.downloadBackup': 'Download backup',
  'boot.lockedTitle': 'OKANE is open somewhere else',
  'boot.lockedLead': 'Another tab or window in this browser is using your data. Close it, then try again — two copies at once would overwrite each other.',
  'boot.movingCloud': 'Moving your financial data to the cloud',
  'boot.movingDevice': 'Moving your financial data to this device',
  'boot.movingLead': 'Copying it across and checking every row before the old copy is deleted. Please keep the app open.',
  'boot.moveFailedTitle': 'Your financial data did not move',
  'boot.moveFailedLead': 'Nothing was deleted — your financial data is still where it was, exactly as it was.',
  'boot.keepHere': 'Keep it on this device',
  'boot.fatalTitle': 'OKANE could not start',
  'boot.needConnection': 'Cloud mode needs a connection — or keep your data on this device instead.',
  'boot.serverBehind': "The server's copy of your financial data is at schema v{server}; this app expects v{app}. The server is being updated — try again in a minute.",
  'common.xOfY': '{x} of {y}',

  'range.allTime': 'All time',
  'range.from': 'From {day}',
  'range.upTo': 'Up to {day}',
  'range.change': 'Date range: {label}. Change it',
  'range.title': 'Date range',
  'range.fromLabel': 'From',
  'range.toLabel': 'To',
  'range.preset.thisMonth': 'This month',
  'range.preset.last7': 'Last 7 days',
  'range.preset.last30': 'Last 30 days',
  'range.preset.last3Months': 'Last 3 months',
  'range.preset.thisYear': 'This year',

  'goals.title': 'Savings goals',
  'goals.reached': 'Goal reached',
  'goals.toGo': '{amount} to go',
  'goals.pace': '{amount} {cadence} · next {day}',
  'goals.reachedOf': '{done} of {total} reached',
  'goals.empty': 'Decide what you are saving for',
  'goals.done': 'done',

  'alerts.title': 'Off plan',
  'alerts.seen': '{matched}/{total} planned items seen',
  'alerts.less': 'Show less',
  'alerts.more': {
    one: 'Show {count} more',
    other: 'Show {count} more',
  },
  'alerts.allWell': 'Everything is going to plan',
  'alerts.allWellDetail': '{matched} of {total} planned items accounted for.',

  'compare.title': 'This month vs last month',
  'compare.titleThen': '{month} vs {previous}',
  'compare.income': 'Income',
  'compare.spent': 'Spending',
  'compare.saved': 'Savings',
  'compare.top': 'Biggest category',
  'compare.lastMonth': 'last month',
  'compare.monthBefore': 'the month before',
  'compare.up': 'up {percent}% from {than}',
  'compare.down': 'down {percent}% from {than}',
  'compare.same': 'same as {than}',
  'compare.new': 'nothing {than}',
  'compare.none': 'nothing either month',
  'compare.was': 'was {amount}',
  'compare.nothing': 'None',
  'section.hide': 'Hide {name}',
  'section.show': 'Show {name}',
  'stats.title': 'Baseline stats',
  'stats.balance': 'Balance',
  'stats.projected': 'Projected month end',
  'stats.allowance': 'Daily allowance',
  'stats.daysLeft': {
    one: '{count} day left',
    other: '{count} days left',
  },
  'stats.leftToday': '{amount} left today',
  'stats.thisMonth': 'This month',
  'stats.thatMonth': 'That month',
  'stats.spent': '{amount} spent',
  'stats.net': '{amount} net',
  'stats.stillToPay': 'Still to pay',
  'stats.stillToPayLabel': 'Planned spending still to pay',
  'stats.allPaid': 'All paid!',
  'stats.nothingPlanned': 'Nothing planned',
  'stats.paidSoFar': '{amount} paid so far',
  'stats.plannedMeter': '{percent}% of this month\'s planned spending is paid',
  'stats.budgetsLabel': 'Budgets',
  'stats.exactlyOnBudget': 'Exactly on budget',
  'stats.over': 'over',
  'stats.under': 'under',
  'stats.budgetsSet': {
    one: '{count} set',
    other: '{count} set',
  },
  'stats.noneYet': 'None yet',
  'stats.offTrack': {
    one: '{count} off track',
    other: '{count} off track',
  },
  'stats.budgetsHint': 'Hold a category to a figure',
  'stats.budgetMeter': '{percent}% of everything you budgeted this month',
  'stats.nextIn': 'Next month in',
  'stats.nextOut': 'Next month out',
  'stats.nextInLabel': 'Planned income for {month}',
  'stats.nextOutLabel': 'Planned spending for {month}',
  'stats.plannedFor': 'planned for {month}',
  'stats.plannedItems': {
    one: '{count} planned item',
    other: '{count} planned items',
  },
  'stats.saved': 'Saved',
  'stats.savedOf': 'of {amount}',
  'stats.savedThisMonth': '{amount} this month',
  'stats.noGoals': 'No savings goals yet',
  'stats.savedMeter': '{percent}% of everything you are saving towards',

  'common.edit': 'Edit',

  'ledger.title': 'Transactions',
  'ledger.shown': {
    one: '{count} shown',
    other: '{count} shown',
  },
  'ledger.more': 'Show more',
  'ledger.loading': 'Loading…',
  'ledger.in': '{amount} in',
  'ledger.out': '{amount} out',
  'ledger.sortedBy': 'Sorted by {sort}, {dir}. Change the sorting',
  'ledger.ascending': 'ascending',
  'ledger.descending': 'descending',
  'ledger.select': 'Select',
  'ledger.editAll': 'Edit all',
  'ledger.duplicates': 'Duplicates',
  'duplicates.title': 'Possible duplicates',
  'duplicates.subtitle': 'The same payment type, a similar amount and description, close together in time.',
  'duplicates.found': {
    one: '{count} pair looks like the same payment twice.',
    other: '{count} pairs look like the same payment twice.',
  },
  'duplicates.daysApart': {
    one: '{count} day apart',
    other: '{count} days apart',
  },
  'duplicates.similarity': 'Similarity, at least',
  'duplicates.days': 'Days apart, at most',
  'duplicates.amountApart': 'Amounts apart, at most',
  'duplicates.configRange': 'Similarity and amounts are 0–100%, days 0–365.',
  'duplicates.configHint': 'The payment type always has to match, and money in never pairs with money out. Amounts 0% apart match to the cent.',
  'duplicates.alike': '{percent} alike',
  'duplicates.amountsDiffer': 'amounts {percent} apart',
  'duplicates.none': 'No duplicates found',
  'duplicates.noneHint': 'No transaction matches another closely enough.',
  'ledger.selected': '{n} of {of} selected',
  'ledger.none': 'None',
  'ledger.all': 'All',
  'ledger.empty': 'No transactions here yet',
  'ledger.emptySearch': 'Nothing matches that search',
  'ledger.emptyHint': 'Record what actually happened — OKANE will match it against your plan.',
  'ledger.emptySearchHint': 'Try a different term, or widen the range to all time.',
  'ledger.addTransaction': 'Add a transaction',

  'kind.income': 'Income',
  'kind.expense': 'Expense',
  'kind.saving': 'Saving',

  'row.alreadySaved': 'Already saved',
  'row.setAside': 'Set aside',
  'row.unplanned': 'Unplanned',

  'tabs.plan': 'Plan',
  'tabs.goals': 'Goals',

  'plan.title': 'The plan',
  'plan.seen': '{matched}/{total} seen',
  'plan.missing': {
    one: '{count} missing',
    other: '{count} missing',
  },
  'plan.offAmount': {
    one: '{count} off amount',
    other: '{count} off amount',
  },
  'plan.nothing': 'Nothing scheduled that month',
  'plan.add': 'New plan',
  'plan.auto': {
    one: 'auto, last one',
    other: 'auto, average of {count}',
  },
  'plan.autoLearning': 'auto, no history yet',
  'plan.addLabel': 'Add a plan',
  'plan.income': 'Planned income',
  'plan.expenses': 'Planned expenses',
  'plan.empty': 'No plans for this month',
  'plan.emptyHint': 'Add your salary and your recurring bills — OKANE then tells you when reality drifts.',
  'plan.seenOn': 'Seen {day}',
  'plan.due': 'Due {day} · {relative}',

  'budgets.nothing': 'Nothing budgeted that month',
  'budgets.offTrackOf': '{off} of {total} off track',
  'budgets.onTrack': {
    one: '{count} on track',
    other: '{count} on track',
  },
  'budgets.allOnTrackShort': 'all on track',
  'budgets.add': 'New budget',
  'budgets.addLabel': 'Add a budget',
  'budgets.empty': 'No budgets this month',
  'budgets.emptyHint': 'Hold some categories or tags to a figure — everything they cover, added up over the whole period.',
  'budgets.meter': '{category}, {percent}% of budget',
  'budgets.auto': {
    one: 'auto, last period',
    other: 'auto, average of {count}',
  },
  'budgets.editLabel': 'Edit the {category} budget',

  'charts.title': 'Trends',
  'charts.tags': 'Tags',
  'charts.categories': 'Categories',
  'charts.oneTag': 'tag',
  'charts.oneCategory': 'category',
  'charts.balance': 'Balance',
  'charts.runningTotal': 'Running total',
  'charts.balanceCaption': 'Account balance day by day, {window}',
  'charts.balanceCaptionSubset': 'Running total of the transactions shown day by day, {window}',
  'charts.flow': 'Money in and out',
  'charts.flowSaved': 'Money in, out and saved',
  'charts.flowCaption': 'Money in and out, {window}',
  'charts.flowSavedCaption': 'Money in, out and put by, {window}',
  'charts.in': 'In',
  'charts.out': 'Out',
  'charts.savedSeries': 'Saved',
  'charts.everyCategory': 'Every category',
  'charts.treemapCaption': 'Every category by all the money it moved, in or out, {window}',
  'charts.treemapCaptionFor': 'Every category by all the money it moved, for {subject}, {window}',
  'charts.nothingMoved': 'No money moved in this window',
  'charts.uncategorised': 'Uncategorised',
  'charts.other': 'Other',
  'charts.whereItGoes': 'Where it goes',
  'charts.whereItComesFrom': 'Where it comes from',
  'charts.spendingByCategory': 'Spending by category',
  'charts.incomeByCategory': 'Income by category',
  'charts.nothingOut': 'Nothing went out here',
  'charts.nothingIn': 'Nothing came in here',
  'charts.splitCaption': '{caption}, {window}',
  'charts.splitCaptionFor': '{caption} for {subject}, {window}',
  'charts.collapse': 'Collapse the charts',
  'charts.expand': 'Expand the charts',
  'charts.day': 'Day',
  'charts.month': 'Month',
  'charts.needAllTime': 'Switch the transactions to all time first',
  'charts.filtered': 'filtered',
  'charts.all': 'All',
  'charts.needALine': 'Turn a tag or category on first',
  'charts.maxLines': '{count} lines at a time.',
  'charts.chipLabel': '{name}, {family}',

  'cadence.once': 'One-off',
  'cadence.weekly': 'Weekly',
  'cadence.fortnightly': 'Every 2 weeks',
  'cadence.monthly': 'Monthly',
  'cadence.quarterly': 'Every 3 months',
  'cadence.yearly': 'Yearly',

  'status.matched': 'Matched',
  'status.over': 'Over plan',
  'status.under': 'Under plan',
  'status.missing': 'Not seen',
  'status.due': 'Due soon',
  'status.upcoming': 'Upcoming',

  'budgetStatus.upcoming': 'Not started',
  'budgetStatus.learning': 'No history yet',
  'budgetStatus.running': 'In progress',
  'budgetStatus.over': 'Over budget',
  'budgetStatus.limit': 'Over the limit',
  'budgetStatus.short': 'Short',
  'budgetStatus.missed': 'Missed',
  'budgetStatus.under': 'Under budget',
  'budgetStatus.matched': 'On budget',
  'budgetStatus.matched.income': 'On target',

  'common.confirmDelete': 'Tap to confirm',

  'goalSheet.new': 'New savings goal',
  'goalSheet.edit': 'Edit goal',
  'goalSheet.subtitle': 'How much, and how quickly.',
  'goalSheet.goal': 'Goal',
  'goalSheet.goalPlaceholder': 'Japan trip, new laptop, safety net…',
  'goalSheet.target': 'Target',
  'goalSheet.alreadySaved': 'Already saved',
  'goalSheet.byDate': 'By a date',
  'goalSheet.byAmount': 'Fixed amount',
  'goalSheet.reachBy': 'Reach it by',
  'goalSheet.putAside': 'Put aside {cadence}',
  'goalSheet.setAside': 'Set aside',
  'goalSheet.starting': 'Starting',
  'goalSheet.setsAside': 'Sets aside {amount} {cadence}',
  'goalSheet.times': {
    one: '{count} time',
    other: '{count} times',
  },
  'goalSheet.lastOn': ', the last on {day}',
  'goalSheet.reaches': {
    one: 'Reaches the target on {day}, after {count} instalment.',
    other: 'Reaches the target on {day}, after {count} instalments.',
  },
  'goalSheet.dayOne': 'day one',
  'goalSheet.countsAsSaved': '{amount} of it counts as saved today, so that much comes straight out of your balance. Nothing moves — no transaction is recorded.',
  'goalSheet.archive': 'Archive this goal',
  'goalSheet.needName': 'Name the goal.',
  'goalSheet.needTarget': 'Set a target amount.',
  'goalSheet.needInstalment': 'Set how much you will put aside each time.',
  'goalSheet.updated': 'Goal updated',
  'goalSheet.added': 'Goal added',
  'goalSheet.deleted': 'Goal deleted',

  'common.category': 'Category',
  'common.amount': 'Amount',
  'common.use': 'Use',
  'common.endsOn': 'Ends on',
  'common.optional': '(optional)',

  'tolerance.exact': 'Exact',

  'planSheet.edit': 'Edit plan',
  'planSheet.subtitle': 'A category, on a schedule: what you expect to see, and when.',
  'planSheet.plannedExpense': 'Planned expense',
  'planSheet.categoryPlaceholder': 'Electricity, Rent, Salary…',
  'planSheet.repeats': 'Repeats',
  'planSheet.firstDue': 'First due on',
  'planSheet.monthlyEquivalent': '{amount} a month equivalent',
  'planSheet.nextDue': 'Next due {day}',
  'planSheet.noFuture': 'No future occurrences',
  'planSheet.howItWorks': 'How OKANE recognises it',
  'planSheet.howItWorksDetail': 'Every transaction in this category, pointing the right way, counts towards whichever occurrence it falls nearest. Nothing is stored and nothing is guessed: re-file a transaction and it answers to a different plan.',
  'planSheet.flagIfOff': 'Flag it if off by',
  'planSheet.archive': 'Archive — stop expecting this',
  'planSheet.needCategory': 'Pick the category this plan is about.',
  'planSheet.auto': 'Set the amount from past occurrences',
  'planSheet.autoPeriods': 'Occurrences to average',
  'planSheet.autoAverage': 'Average',
  'planSheet.autoPickFirst': 'Pick the category to see the figure.',
  'planSheet.autoNoHistory': 'Nothing came in for the last ones, so no figure until something does.',
  'planSheet.autoShort': {
    one: 'Only {count} of them was paid, so that is all it averages — the rest never came.',
    other: 'Only {count} of them were paid, so that is all it averages — the rest never came.',
  },
  'planSheet.needPeriods': 'Average over at least 1 occurrence, at most 120.',
  'planSheet.needAmount': 'Enter the planned amount.',
  'planSheet.updated': 'Plan updated',
  'planSheet.added': 'Plan added',
  'planSheet.deleted': 'Plan deleted',

  'budgetSheet.edit': 'Edit budget',
  'budgetSheet.subtitle': 'Some categories and tags, over a whole period: what they should all add up to.',
  'budgetSheet.limit': 'Spending limit',
  'budgetSheet.target': 'Income target',
  'budgetSheet.categories': 'Categories',
  'budgetSheet.coversHint': 'A transaction counts if it is in any of these categories or wears any of these tags — once, however many it matches.',
  'budgetSheet.categoryPlaceholder': 'Groceries, Fun, Freelance…',
  'budgetSheet.every': 'Every',
  'budgetSheet.inEffect': 'In effect from',
  'budgetSheet.periodRuns': 'This period runs {from} — {to}',
  'budgetSheet.howItWorks': 'How OKANE counts it',
  'budgetSheet.howItWorksDetail': 'Every transaction it covers, {direction}, is added up over the whole period and measured against this one figure. Nothing can be late, because nothing is expected on any particular day — only the total can be wrong.',
  'budgetSheet.comingIn': 'coming in',
  'budgetSheet.goingOut': 'going out',
  'budgetSheet.warnShort': 'Warn if short by',
  'budgetSheet.warnOver': 'Warn if over by',
  'budgetSheet.exactNote': 'Anything off the figure is flagged.',
  'budgetSheet.roomNote': '{amount} either way passes without a word.',
  'budgetSheet.archive': 'Archive — stop counting this',
  'budgetSheet.needCategory': 'Pick at least one category or tag for this budget to cover.',
  'budgetSheet.needAmount': 'Enter the budgeted amount.',
  'budgetSheet.needPeriods': 'Average over at least 1 period, at most 120.',
  'budgetSheet.auto': 'Set the amount from past periods',
  'budgetSheet.autoPeriods': 'Periods to average',
  'budgetSheet.autoPickFirst': 'Pick what it covers to see the figure.',
  'budgetSheet.autoAverage': 'Average',
  'budgetSheet.autoShort': {
    one: 'Your transactions only go back {count} period, so that is all it averages.',
    other: 'Your transactions only go back {count} periods, so that is all it averages.',
  },
  'budgetSheet.autoNoHistory': 'No transactions before this period yet, so no figure until there are.',
  'budgetSheet.updated': 'Budget updated',
  'budgetSheet.added': 'Budget added',
  'budgetSheet.deleted': 'Budget deleted',

  'common.close': 'Close',
  'common.renameThing': 'Rename {name}',
  'common.deleteThing': 'Delete {name}',
  'common.searchCategories': 'Search categories…',
  'common.searchTags': 'Search tags…',

  'picker.search': 'Search…',
  'picker.chosen': 'chosen',
  'picker.addAnother': 'Add another…',
  'picker.clear': 'Clear {name}',
  'picker.hide': 'Hide the list',
  'picker.show': 'Show the list',
  'picker.noMatch': 'Nothing matches that.',
  'picker.emptyList': 'Nothing here yet — type a name.',

  'txSheet.new': 'New transaction',
  'txSheet.edit': 'Edit transaction',
  'txSheet.subtitle': 'What actually happened.',
  'txSheet.spent': 'Spent',
  'txSheet.received': 'Received',
  'txSheet.description': 'Description',
  'txSheet.descriptionPlaceholder': 'Where did it go?',
  'txSheet.date': 'Date',
  'txSheet.notes': 'Notes',
  'txSheet.paymentType': 'Payment type',
  'txSheet.paymentTypePlaceholder': 'Card, transfer… (optional)',
  'txSheet.needAmount': 'Enter an amount.',
  'txSheet.updated': 'Transaction updated',
  'txSheet.added': 'Transaction added',
  'txSheet.deleted': 'Transaction deleted',

  'picker.make': 'Make',

  'charts.nothingYet': 'Nothing to chart yet',

  'goals.newGoal': 'New goal',

  'ledger.flipHint': 'Pick the same one again to flip it.',

  'sort.date': 'Date',
  'sort.description': 'Description',
  'sort.amount': 'Amount',

  'backup.title': 'Back up',
  'backup.subtitle': 'A copy of the whole database, saved to this device.',
  'backup.password': 'Password',
  'backup.again': 'And again',
  'backup.passwordPlaceholder': 'At least six characters',
  'backup.passwordHint': 'Six characters or more.',
  'backup.mismatch': 'Those two do not match.',
  'backup.downloaded': 'Backup downloaded',
  'backup.downloadedProtected': 'Backup downloaded, password-protected',

  'categorySheet.subtitle': 'What a transaction is, and the tags that come with it.',
  'categorySheet.tagsOn': 'Tags on {name}',
  'categorySheet.newCategory': 'New category',
  'categorySheet.empty': 'No categories yet. Make one above, or just type one on a transaction.',
  'categorySheet.created': 'Category created',
  'categorySheet.renamed': 'Renamed everywhere',
  'categorySheet.deleted': 'Category deleted',
  'categorySheet.exists': '“{name}” already exists.',
  'categorySheet.tagsOnEverything': 'Tags on everything in {name}',

  'bulk.title': {
    one: 'Edit {count} transaction',
    other: 'Edit {count} transactions',
  },
  'bulk.subtitle': '{amount} altogether. Only what you switch on is changed.',
  'bulk.setCategory': 'Set the category',
  'bulk.noCategory': 'Nothing picked — applying now takes the category off all of them.',
  'bulk.setDescription': 'Set the description',
  'bulk.setNotes': 'Set the notes',
  'bulk.setPaymentType': 'Set the payment type',
  'bulk.descriptionPlaceholder': 'What these all were',
  'bulk.applying': 'Applying…',
  'bulk.updated': {
    one: '{count} transaction updated',
    other: '{count} transactions updated',
  },
  'bulk.deleted': {
    one: '{count} transaction deleted',
    other: '{count} transactions deleted',
  },

  'console.subtitle': 'Straight at the database. Nothing here asks twice.',
  'console.tables': 'Tables',
  'console.columnsOf': 'Columns of {name}',
  'console.statement': 'Statement',
  'console.placeholder': 'SELECT * FROM transactions ORDER BY date DESC LIMIT 20;',
  'console.writes': 'This one writes — there is no undo but your last backup.',
  'console.recent': 'Recent',
  'console.rows': {
    one: '{count} row',
    other: '{count} rows',
  },
  'console.changed': {
    one: '{count} row changed',
    other: '{count} rows changed',
  },
  'console.noColumns': 'No rows came back, so there are no columns to name.',
  'console.footnote': 'Runs as written, against your real data.',
  'console.run': 'Run',
  'console.running': 'Running…',

  'import.title': 'Import a statement',
  'import.subtitle': 'An .xlsx or .xls export from your bank. The file never leaves this device.',
  'import.field.date': 'Date',
  'import.field.description': 'Description',
  'import.field.amount': 'Amount',
  'import.field.type': 'Payment type',
  'import.field.credit': 'Money in',
  'import.field.debit': 'Money out',
  'import.drop': 'Drop the file here',
  'import.chooseFile': 'Choose a file',
  'import.chooseAnother': 'Choose another',
  'import.reading': 'Reading…',
  'import.sheet': 'Sheet',
  'import.columns': 'Columns',
  'import.datesReadAs': 'Dates read as',
  'import.dayFirst': 'Day first',
  'import.monthFirst': 'Month first',
  'import.ready': 'Ready to import',
  'import.period': 'Period',
  'import.alreadyHere': 'Already in OKANE',
  'import.unreadableRows': 'Rows without a date or amount',
  'import.nothingReadable': 'Nothing readable in this sheet. Check the column mapping above — OKANE needs at least a date and an amount.',
  'import.otherCurrency': 'This statement is in {currency}',
  'import.otherCurrencyDetail': 'OKANE is set to {current}. Amounts are imported as they are — switch OKANE to {other} so they read correctly.',
  'import.footnote': 'Imported rows are matched against your plans automatically.',
  'import.clearPending': 'Clear pending transactions',
  'import.updatePending': 'Update pending',
  'import.pendingSettled': {
    one: '{count} settled since: brought up to date in place, keeping its category and tags.',
    other: '{count} settled since: brought up to date in place, keeping their categories and tags.',
  },
  'import.pendingGone': {
    one: '{count} no longer on this statement: removed — the bank dropped it, or settled it as another row.',
    other: '{count} no longer on this statement: removed — the bank dropped them, or settled them as other rows.',
  },
  'import.importing': 'Importing…',
  'import.importN': 'Import {count}',
  'import.imported': {
    one: '{count} transaction imported',
    other: '{count} transactions imported',
  },

  'settings.tab.general': 'General',
  'settings.tab.suggestions': 'Suggestions',
  'settings.tab.sync': 'Sync',
  'settings.tab.data': 'Your data',
  'settings.tab.usage': 'Usage',
  'usage.lead': 'How OKANE is used: anonymous daily totals from every device, kept on the OKANE server. Visitors are counted per day without knowing who they are, so the same person on two days counts twice.',
  'usage.span': 'Over',
  'usage.lastDays': {
    one: 'the last day',
    other: 'the last {count} days',
  },
  'usage.refresh': 'Refresh',
  'usage.visitors': 'Visitors',
  'usage.visitorDays': 'Visitors, day by day',
  'usage.busiest': 'Busiest day',
  'usage.opens': 'Opens',
  'usage.chartCaption': 'Visitors and opens per day',
  'usage.nothing': 'Nothing counted in this span yet.',
  'usage.more': '+{count} more',
  'settings.subtitle': 'How OKANE counts your money.',
  'settings.currency': 'Currency',
  'settings.opening': 'Opening balance',
  'settings.openingHint': 'What was in the account before the first transaction.',
  'settings.currentBalance': 'Current balance',
  'settings.currentBalanceHint': 'What your bank shows right now. Type it in and OKANE moves the opening balance to match — worth doing once your history is imported.',
  'settings.countUpcoming': 'Count income that has not arrived yet',
  'settings.countUpcomingHint': 'On — spend against the salary you are expecting. Off — only against money already in the account.',
  'settings.storage.opfs': 'Kept as a file in this browser\'s private storage.',
  'settings.storage.indexeddb': 'Kept in this browser\'s database storage — it cannot keep files.',
  'settings.storage.native': 'Kept as a file on this device.',
  'settings.storage.protected': 'Protected: the browser will not clear it to free up space.',
  'settings.storage.unprotected': 'Not protected: the browser may clear it when space runs low. Installing OKANE on your home screen usually changes that — and keep a backup.',
  'settings.yourDataHint': 'Everything lives in a SQLite database on this device. Your financial data is never uploaded.',
  'settings.lockedFile': '{name} is password-protected.',
  'settings.itsPassword': 'Its password',
  'settings.restore': 'Restore',
  'settings.sampleData': 'Sample data',
  'settings.loading': 'Loading…',
  'settings.eraseAll': 'Erase all',
  'settings.balanceMatched': 'Opening balance adjusted to match',
  'settings.restored': 'Backup restored',
  'settings.sampleLoaded': 'Sample data loaded',
  'settings.erased': 'Everything erased',
  'settings.footer': 'OKANE · offline-first · SQLite {transactions} transactions, {plans} plans, {goals} goals',
  'settings.openingFile': 'Opening…',

  'kindLabel.income': 'Received',
  'kindLabel.expense': 'Spending',
  'kindLabel.savings': 'Saving',

  'filter.status': 'Status',
  'filter.pending': 'Pending',
  'filter.settled': 'Settled',
  'filter.kind': 'Kind',
  'filter.tag': 'Tag',
  'filter.category': 'Category',
  'filter.dateFrom': 'Date from',
  'filter.dateTo': 'Date to',
  'filter.amountFrom': 'Amount from',
  'filter.amountTo': 'Amount to',
  'filter.nothing': 'Nothing',
  'filter.or': 'or',

  'alert.budgetOver': '{category} is {amount} over budget',
  'alert.budgetOverLimit': '{category} is {amount} over the limit',
  'alert.budgetShort': '{category} came in {amount} short',
  'alert.budgetDetailRunning': '{actual} of {expected} so far this period.',
  'alert.budgetDetailDone': '{actual} of {expected} over the period.',
  'alert.notArrived': '{category} hasn\'t arrived',
  'alert.notShownUp': '{category} hasn\'t shown up',
  'alert.missingDetail': '{amount} was due {when}. No matching transaction yet.',
  'alert.dueToday': 'today',
  'alert.dueDaysAgo': {
    one: '{count} day ago',
    other: '{count} days ago',
  },
  'alert.cameInHigher': '{category} came in {amount} higher',
  'alert.cameInLower': '{category} came in {amount} lower',
  'alert.varianceDetail': 'Planned {expected}, actually {actual}.',
  'alert.incomeShort': 'Income is {amount} short of plan',
  'alert.incomeOver': 'Income is {amount} above plan',
  'alert.incomeDetail': '{actual} received against {planned} planned so far this month.',
  'alert.monthShort': 'Projected to end the month {amount} short',
  'alert.monthShortDetail': 'Commitments still due exceed the balance plus expected income.',
  'alert.noneLeft': 'No free spending left this month',
  'alert.noneLeftDetail': 'Everything remaining is spoken for by plans and savings.',
  'alert.overToday': '{amount} over today\'s allowance',
  'alert.overTodayDetail': 'Spent {spent} against {perDay} a day.',

  'settings.language': 'Language',

  'budgets.title': 'Budgets',

  'tagSheet.subtitle': 'Your own labels, on as many transactions as you like.',
  'tagSheet.newTag': 'New tag',
  'tagSheet.placeholder': 'Holiday, Work, Shared…',
  'tagSheet.empty': 'No tags yet. Make one above, then put it on a transaction.',
  'tagSheet.created': 'Tag created',
  'tagSheet.deleted': 'Tag deleted',

  'suggest.title': 'Looks like',
  'suggest.applyAll': 'Use all of these',
  'suggest.reviewTitle': 'What these look like',
  'suggest.reviewSubtitle': {
    one: '{count} imported row OKANE recognises from your transactions. Untick anything it got wrong.',
    other: '{count} imported rows OKANE recognises from your transactions. Untick anything it got wrong.',
  },
  'suggest.acceptAll': 'Tick everything suggested',
  'suggest.clearAll': 'Untick everything',
  'suggest.apply': {
    one: 'Label {count} row',
    other: 'Label {count} rows',
  },
  'suggest.applied': {
    one: '{count} row labelled',
    other: '{count} rows labelled',
  },
  'suggest.cancelKeeps': 'Cancelling keeps the import and leaves the labels off.',
  'suggest.nothingToSay': 'Nothing here looks like any transaction seen before.',

  'lab.scoreboard': 'How it is doing',
  'lab.notEnough': 'Only {count} filed rows so far — too few to mark it on. File a few dozen and come back.',
  'lab.categoryPrecision': 'Category right',
  'lab.categoryCoverage': 'Category answered',
  'lab.tagPrecision': 'Tags right',
  'lab.tagRecall': 'Tags found',
  'lab.f1': 'Tag F1',
  'lab.split': 'Trained on {trained} rows, marked on the {tested} most recent.',
  'lab.features': 'What it reads',
  'lab.sweep': 'Sweep',
  'lab.sweeping': 'Sweeping…',
  'lab.sweepOf': 'Sweep of {name}',
  'lab.value': 'Value',
  'lab.useBest': 'Use {value}',
  'lab.reset': 'Back to defaults',
  'lab.footnote': 'Nothing here leaves the device, and nothing is trained until a suggestion is asked for. Every setting is saved, so an experiment survives a reload.',
  'lab.learningRate': 'Learning rate',
  'lab.learningRateHint': 'How far each example moves the weights. Too high and it never settles.',
  'lab.epochs': 'Passes over the transactions',
  'lab.epochsHint': 'More passes fit the history closer, and eventually fit its accidents.',
  'lab.l2': 'Weight decay (L2)',
  'lab.l2Hint': 'Pulls weights back towards zero, so one lucky word cannot decide everything.',
  'lab.threshold': 'Confidence to suggest',
  'lab.thresholdHint': 'Below this the model keeps quiet. Higher suggests less, and is wrong less.',
  'lab.minExamples': 'Examples before a label counts',
  'lab.minExamplesHint': 'A label seen once or twice is a coincidence, not a pattern.',
  'lab.minTokenLength': 'Shortest word worth reading',
  'lab.minTokenLengthHint': 'Drops the noise a statement is full of — single letters, stray digits.',
  'lab.maxTags': 'Most tags to suggest at once',
  'lab.maxTagsHint': 'A row wearing nine guessed tags is not a suggestion, it is a mess.',
  'lab.bigrams': 'Word pairs',
  'lab.bigramsHint': '"air serbia" as one clue rather than two, which separates it from "air fryer".',
  'lab.prefixes': 'Word beginnings',
  'lab.prefixesHint': 'Four letters of each word, so MERCATOR and MERCATOR-S read alike.',
  'lab.amount': 'How much it was',
  'lab.amountHint': 'Rounded to an order of magnitude: rent and coffee do not look alike.',
  'lab.weekday': 'Which day of the week',
  'lab.weekdayHint': 'Sometimes real — a Friday shop — and sometimes an accident of the calendar.',

  'settings.suggestHint': 'OKANE learns what your transactions are from the ones you have already filed.',

  'stats.inTheAccount': 'In the account',

  'importer.generic': 'Generic spreadsheet',

  'import.bank': 'Statement from',
  'import.settled': 'Settled since last export',
  'import.settledCount': {
    one: '{count} row, already here on another date',
    other: '{count} rows, already here on other dates',
  },

  'importer.intesa': 'Banca Intesa',

  'import.bankInSettings': 'Set in Settings, beside the currency.',

  'settings.bank': 'Bank',
  'settings.bankHint': 'How OKANE reads a statement you import.',

  'import.pending': 'Not settled yet',
  'import.pendingTag': 'pending',
  'import.settlesTag': 'settles one here',
  'import.alreadyHereTag': 'already here',

  'row.pending': 'pending',
  // --- Help ------------------------------------------------------------------
  'help.title': 'How OKANE works',
  'help.step': 'Step {n} of {of}',
  'help.steps': 'Steps',
  'help.goTo': 'Step {n}: {title}',
  'help.next': 'Next',
  'help.back': 'Back',
  'help.openSettings': 'Open settings',

  'help.transactions.title': 'Transactions',
  'help.transactions.lead': 'Everything starts with a transaction: one amount of money that came in or went out, on one day.',
  'help.transactions.p1': 'Each has a date, an amount and a description, and can carry a category, tags, a payment type and notes. Money coming in is green.',
  'help.transactions.p2': 'The balance, the charts, the plan and the budgets are all worked out from these rows. Change a transaction and everything follows at once.',
  'help.transactions.p3': 'A row marked PENDING is a card payment your bank has not finished with; it settles when a later statement arrives. Purple rows are what your savings goals set aside — shown in the list, but not money that moved.',

  'help.inOut.title': 'Getting your money in and out',
  'help.inOut.lead': 'Everything lives in a database on this device. Your financial data is never uploaded, so moving it is always something you do yourself.',
  'help.inOut.p1': 'Add a transaction by hand with the + button in the corner, or Add above the list.',
  'help.inOut.p2': 'Import a bank statement (.xlsx or .xls) with the import button in the header. Choose your bank in Settings first: it decides how the file is read. Rows you already have are recognised and skipped.',
  'help.inOut.p3': 'Back up from Settings → Back up: the whole database in one password-protected file. Restore it on this or another device from Settings → Restore.',
  'help.inOut.p4': 'For anything else there is the SQL console in the header — the real database, to query directly.',

  'help.categories.title': 'Categories',
  'help.categories.lead': 'A category says what a transaction was for. Each transaction has one at most.',
  'help.categories.p1': 'Plans and budgets work by category: a plan expects something in Rent around the 1st, a budget holds Groceries and Dining out to one figure a month.',
  'help.categories.p2': 'Typing a new name anywhere creates it. Rename or delete categories from the folder icon in the header; a rename carries every transaction with it.',
  'help.categories.p3': 'A category can carry tags that everything filed under it wears. Each savings goal is a category of its own too, so it can be charted and filtered.',
  'help.categories.p4': 'Once you have filed a few, OKANE starts suggesting a category for new rows.',

  'help.tags.title': 'Tags',
  'help.tags.lead': 'Tags are labels, and a transaction can wear as many as it likes — work, holiday, tax-deductible.',
  'help.tags.p1': 'A category answers what something was; tags answer whatever else you will want to ask later.',
  'help.tags.p2': 'Put them on one transaction, on many at once, or on a category so everything in it wears them. Manage them from the tag icon in the header.',
  'help.tags.p3': 'Tags and categories can both be drawn as lines on the charts — pick them under the charts — and both can be searched and filtered by.',

  'help.batches.title': 'Filing hundreds at once',
  'help.batches.lead': 'After an import there may be hundreds of rows with no category. Nobody files them one by one.',
  'help.batches.p1': 'Narrow the list first. Type words to search the descriptions, or filters like the ones below — only the uncategorised, only spending, only large amounts. Suggestions appear as you type.',
  'help.batches.p2': 'Then press Edit all: everything the list is showing is edited together. Set the category, add or take off tags, set a payment type or notes.',
  'help.batches.p3': 'Repeat for the next pattern — "lidl" to Groceries, "shell" to Transport. Use Select to pick rows by hand instead, and Duplicates to find anything imported twice.',

  'help.ready.title': 'Good luck, and have fun',
  'help.ready.lead': 'That is all of it. This help is always one tap away, behind the ? in the header.',
  'help.ready.p1': 'Start in Settings: choose your currency and your bank, and once your history is in, type your current balance so the numbers match your bank.',
  'help.ready.p2': 'Or, while OKANE is still empty, load the Sample data there to look around first. Erase all takes it away again.',
  // --- Cloud -----------------------------------------------------------------
  'settings.cloudHintLocal': 'This device keeps its own data. Move it to the cloud to use it on every device, or to share it.',
  'settings.cloudHintCloud': 'Signed in as {email} · {org}.',
  'settings.storage.cloud': 'Kept on the OKANE server, in your org\'s own database.',
  'settings.yourDataHintCloud': 'Everything lives in your org\'s database on the OKANE server. A backup still downloads a copy to this device.',
  'signin.title': 'Sign in to OKANE',
  'signin.lead': 'Your financial data is kept on the OKANE server. Enter your e-mail and we will send you a link — a new address makes a new account.',
  'signin.moving': 'Once you are in, the financial data on this device moves to the server and is deleted here.',
  'signin.email': 'E-mail',
  'signin.sendLink': 'E-mail me a sign-in link',
  'signin.passkey': 'Sign in with a passkey',
  'signin.local': 'Keep my data on this device instead',
  'signin.sent': 'Check your inbox — a link is on its way to {email}. It works once, for 15 minutes.',
  'signin.noPasskey': 'No passkey was chosen.',
  'signin.language': 'Language',
  'signin.showCode': 'Show a code',
  'signin.scanCode': 'Scan a code',
  'device.back': 'Back',
  'device.showTitle': 'Sign in with your phone',
  'device.showLead': 'On your phone, where you already use OKANE, open Settings → Cloud & account → Add a device → Scan a code, and point it here.',
  'device.compare': 'Your phone will show this number too. Approve only if they match.',
  'device.waiting': 'Waiting for your phone…',
  'device.expired': 'This code has expired.',
  'device.newCode': 'Show a new code',
  'device.scanTitle': 'Scan a code',
  'device.scanLead': 'On a device where you already use OKANE, open Settings → Cloud & account → Add a device → Show a code, and point the camera at it.',
  'device.noCamera': 'The camera could not be opened: {reason}',
  'device.notOurs': 'That is not an OKANE code.',
  'device.signInFirst': 'That code signs in another device — sign in on this phone first, then scan it.',
  'device.signingIn': 'Signing in…',
  'device.sheetTitle': 'Add a device',
  'device.showSheetLead': 'Scan this with the phone you want to add — in OKANE (Scan a code) or with its camera. It works once, for 2 minutes.',
  'device.renewsIn': 'A fresh code in {seconds} s',
  'device.scanSheetLead': 'On the device you want to add, open OKANE and choose Show a code on its sign-in screen. Point the camera at it.',
  'device.approveTitle': 'Sign that device in?',
  'device.approveLead': 'Only if the other screen shows exactly this number:',
  'device.unknown': 'An unknown device',
  'device.approve': 'Sign it in',
  'device.approved': 'The other device is signed in',
  'device.alreadyIn': 'This phone is signed in already. To add another device, scan the code on its sign-in screen.',
  'cloud.devices': 'Devices',
  'cloud.devicesHint': 'Add a phone, tablet or computer to this account with a QR code — the phone always does the scanning.',
  'cloud.joinByCode': 'Already use OKANE on another device? Scan the code it shows (Add a device → Show a code) to open the same profile here. This device\'s own profile stays on it.',
  'cloud.title': 'Cloud',
  'cloud.gainDevices': 'The same financial data on every device, always up to date.',
  'cloud.gainShare': 'Share it — a household, a small business — each person with their own sign-in.',
  'cloud.costRead': 'Your data is kept on our server and is not end-to-end encrypted: we can read it.',
  'cloud.costOffline': 'It needs a connection. In cloud mode the app does not work offline.',
  'cloud.org': 'Organisation',
  'cloud.newOrg': 'New',
  'cloud.newOrgPlaceholder': 'Name of a new organisation',
  'cloud.members': 'Members',
  'cloud.role.owner': 'Owner',
  'cloud.role.member': 'Member',
  'cloud.role.viewer': 'Viewer',
  'cloud.roleFor': 'Role of {email}',
  'cloud.removeFor': 'Remove {email}',
  'cloud.leave': 'Leave this organisation',
  'cloud.emailPlaceholder': 'their@e-mail.com',
  'cloud.addPerson': 'Add',
  'cloud.newRole': 'Their role',
  'cloud.added': 'Added — they sign in with a link sent to that address',
  'cloud.rolesHint': 'Owners manage the organisation and its people; members read and write; viewers only read.',
  'cloud.passkeys': 'Passkeys',
  'cloud.noPasskeys': 'None yet — you sign in with e-mailed links.',
  'cloud.passkeyName': 'Passkey name',
  'cloud.passkeyNamePlaceholder': 'e.g. My phone',
  'cloud.addPasskey': 'Add a passkey',
  'cloud.passkeyAdded': 'Passkey added',
  'cloud.passkeyCancelled': 'No passkey was made.',
  'cloud.removePasskey': 'Remove passkey {name}',
  'cloud.reconnecting': 'Reconnecting to the server…',
  'cloud.moveUpHow': 'Your financial data moves to the server: it is copied across, checked row by row, and then deleted from this device. If you already have a profile in the cloud, this one becomes a profile of its own.',
  'cloud.moveUp': 'Move my financial data to the cloud',
  'cloud.backupFirst': 'Before it moves, keep a copy of your own — a file you can restore anywhere.',
  'cloud.backup': 'Download a backup',
  'cloud.backupProtected': 'With a password…',
  'cloud.backedUp': 'Downloaded',
  'cloud.moveDownTitle': 'Move to this device',
  'cloud.moveDownHow': 'Your financial data is copied onto this device, checked row by row, and then deleted from the server. It works offline again, and only this browser has it.',
  'cloud.moveDown': 'Move my financial data to this device',
  'cloud.moveDownReplace': 'Replace it and move',
  'cloud.moveDownShared': 'Others share this profile, so it stays on the server. Remove them first to take it off — or download a backup to keep a copy.',
  'cloud.moveDownNotOwner': 'Only an owner can take this profile off the server. Download a backup to keep a copy of it.',
  'cloud.moveLocked': 'OKANE is open in another tab in this browser. Close it, then try again.',
  'cloud.movedUp': 'Your financial data is in the cloud now — the copy on this device is deleted',
  'cloud.movedDown': 'Your financial data is on this device now — the copy on the server is deleted',
  'cloud.serverCopyLeft': 'Your financial data is here, but its copy on the server could not be deleted yet. OKANE will try again next time it starts.',
  'cloud.replaceLocal': {
    one: 'This device already holds a profile ({count} transaction). Moving replaces it.',
    other: 'This device already holds a profile ({count} transactions). Moving replaces it.',
  },
  'cloud.signOut': 'Sign out',
  'cloud.denied': 'Your role in this organisation can only read — nothing was changed.',
  'cloud.disconnected': 'The connection to the server dropped, so that was not saved. Try again in a moment.',
  'sync.gainPrivate': 'Your financial data is sealed on this device before it leaves: the server cannot read a single amount.',
  'sync.gainOffline': 'It stays on this device and works offline; changes travel when you are back online.',
  'sync.costLost': 'Forget the passphrase and lose the recovery key, and nobody — not even we — can open it again.',
  'sync.signInFirst': 'Sign in first. This device stays local until your financial data moves: signing in only says who you are.',
  'sync.sendLink': 'E-mail me a link',
  'sync.linkSent': 'A sign-in link is on its way to {email}. Open it on this device.',
  'sync.signedInAs': 'Signed in as {email}',
  'sync.recoveryPlaceholder': 'Recovery key',
  'sync.passphrase': 'Passphrase',
  'sync.passphraseAgain': 'The passphrase again',
  'sync.usePassphrase': 'Use the passphrase instead',
  'sync.useRecovery': 'Forgot it? Use the recovery key',
  'sync.newTitle': 'Start syncing this profile',
  'sync.newLead': 'Choose a passphrase. You will type it on your other devices — or approve them from this one instead.',
  'sync.name': 'Name',
  'sync.defaultName': 'Personal',
  'sync.passphraseShort': 'At least {n} characters.',
  'sync.passphraseDiffers': 'The two passphrases differ.',
  'sync.turnOn': 'Turn on encrypted sync',
  'sync.turningOn': 'Sealing your financial data…',
  'sync.recoveryTitle': 'Your recovery key',
  'sync.recoveryLead': 'Write it down and keep it somewhere safe. It opens your financial data if the passphrase is forgotten, and it is shown only this once.',
  'sync.recoveryDone': 'I have written it down',
  'sync.status.off': 'Not syncing',
  'sync.status.connecting': 'Connecting…',
  'sync.status.syncing': 'Syncing…',
  'sync.status.synced': 'Up to date',
  'sync.status.offline': 'Offline — changes will travel when you are back',
  'sync.status.locked': 'Waiting for approval',
  'sync.status.schema-ahead': 'Another device runs a newer OKANE — update to keep syncing',
  'sync.status.error': 'Syncing failed',
  'sync.editing': '{user} is making changes',
  'sync.lockedLead': 'Open OKANE on a device that has this profile open, go to Settings → Sync, and approve this one if it shows the same code:',
  'sync.waitingTitle': 'Devices asking to join',
  'sync.waitingLead': 'Approve only a device showing the same code — that is what proves it is the one you think.',
  'sync.aDevice': 'A device',
  'sync.approve': 'Approve',
  'sync.approved': '{name} can sync now',
  'sync.now': 'Sync now',
  'sync.why.later-here': 'both changed it; this device’s later change kept',
  'sync.why.later-elsewhere': 'both changed it; the other device’s later change kept',
  'sync.why.deleted-here': 'deleted here, so the other device’s change was dropped',
  'sync.why.deleted-elsewhere': 'deleted elsewhere, so this device’s change was dropped',
  'sync.why.constraint': 'could not be kept: it clashed with another row',
  'sync.wrongPassphrase': 'That is not the passphrase.',
  'sync.wrongRecovery': 'That is not the recovery key.',
  'sync.refused': 'Not in an encrypted profile: this would change it on every device.',
  'sync.conflicts': {
    one: '{count} change merged — see which',
    other: '{count} changes merged — see which',
  },
  'settings.syncTitle': 'End-to-end encrypted sync',
  'settings.cloudTitle': 'Or keep it on our server',
  'sync.hideCode': 'Hide the code',
  'sync.scanDevice': 'Scan a device’s code',
  'sync.scanToJoin': 'Scan a code from a device that syncs',
  'sync.showDeviceCode': 'Show a code to scan instead',
  'sync.qrDeviceLead': 'Scan this with a device that has the profile open: it approves this one, no digits to compare.',
  'sync.scannedVerified': '{name} — its key matches its code',
  'sync.approveElsewhere': 'That code is for a device that has the encrypted profile open: scan it with one.',
  'error.sync-no-device': 'That device is not waiting in this profile.',
  'error.sync-key-mismatch': 'That device’s key is not the one its code shows. Do not approve it.',
  'cloud.encryptTitle': 'Encrypt this profile',
  'cloud.encrypt': 'Encrypt this profile',
  'cloud.encryptLead': 'Make it end-to-end encrypted: the same financial data and the same people, but the server can no longer read it. It is kept on every device and works offline. Each person’s devices must then be approved once — by QR code, or by comparing six digits.',
  'cloud.encryptConfirm': 'Encrypt this profile now? Anything someone else writes in the next few seconds may be lost, and every other device must then be approved once.',
  'cloud.encryptDone': 'Encrypted. Write down the recovery key, then continue: this profile opens as the encrypted one it now is, and the readable copy is gone from the server.',
  'cloud.encryptContinue': 'I have written it down — continue',
  'sync.status.gone': 'You are no longer in this profile',
  'gate.kind': 'End-to-end encrypted profile',
  'gate.lead': 'This device does not hold its key yet. Nothing of it can be shown until it does.',
  'gate.signIn': 'Sign in again',
  'gate.ownerTitle': 'Open it here',
  'gate.open': 'Open the profile',
  'gate.opening': 'Opening…',
  'gate.orApprove': 'Or approve this device from one that has the profile open, if it shows the same code:',
  'gate.openAnother': 'Open another profile',
  'gate.onItsOwn': 'Use this device on its own',
  'gate.signOut': 'Sign out',
  'cloud.encrypted': 'encrypted',
  'sync.status.signed-out': 'Signed out — changes wait here until you sign in',
  'states.title': 'Where your financial data is',
  'states.lead': 'On this device or in the cloud, readable or encrypted. Choose where it should be.',
  'states.readable': 'Readable',
  'states.encrypted': 'Encrypted',
  'states.row.device': 'This device',
  'states.row.cloud': 'The cloud',
  'states.local': 'Local',
  'states.sealed': 'Local, encrypted',
  'states.cloud': 'Cloud',
  'states.vault': 'Cloud, encrypted',
  'states.localLead': 'Only here; opens without a passphrase',
  'states.sealedLead': 'Only here; opens with a passphrase',
  'states.cloudLead': 'On our server, shared; we could read it',
  'states.vaultLead': 'On every device you share it with; we cannot read it',
  'states.here': 'Your financial data is here',
  'states.working': 'Working…',
  'states.twoSteps': 'Two steps: first {first}, then {second}. The second is offered here again once the first is done.',
  'states.step.up': 'move it to the cloud',
  'states.step.down': 'move it to this device',
  'states.step.seal': 'encrypt it here',
  'states.step.unseal': 'decrypt it here',
  'states.step.sealedUp': 'move it to the cloud, encrypted',
  'states.step.sealedDown': 'move it to this device, encrypted',
  'states.step.encrypt': 'encrypt it in the cloud',
  'states.step.decrypt': 'decrypt it in the cloud',
  'states.do.up': 'Your financial data goes to the OKANE server, which can read it; the copy on this device is deleted. You sign in on the way.',
  'states.do.down': 'Your financial data comes onto this device, readable, and is deleted from the server.',
  'states.do.seal': 'Choose a passphrase: this device will ask for it every time OKANE opens. The readable copy is deleted.',
  'states.do.unseal': 'Your financial data is decrypted onto this device and opens without a passphrase from now on.',
  'states.do.sealedUp': 'It becomes an encrypted profile in the cloud with the same passphrase and recovery key; other devices join it with the passphrase or your approval.',
  'states.do.sealedDown': 'It stays on this device only, still encrypted with the same passphrase and recovery key; the encrypted profile in the cloud is deleted.',
  'states.do.encrypt': 'The same financial data, with the same people, becomes end-to-end encrypted; the server deletes the readable copy.',
  'states.do.decrypt': 'The same financial data, with the same people, goes back on the server readable; every device opens it from there.',
  'states.go.up': 'Move to the cloud',
  'states.go.down': 'Move to this device',
  'states.go.seal': 'Encrypt',
  'states.go.unseal': 'Decrypt',
  'states.go.sealedUp': 'Move to the cloud',
  'states.go.sealedDown': 'Move to this device',
  'states.go.encrypt': 'Encrypt this profile',
  'states.go.decrypt': 'Decrypt this profile',
  'states.ownerOnly': 'Only an owner of this profile can do that.',
  'states.shared': 'Others share this profile: it can leave the cloud only when nobody else is in it.',
  'unlock.title': 'Your financial data is encrypted',
  'unlock.lead': 'Type the passphrase to open it on this device.',
  'unlock.open': 'Open',
  'cloud.deleteLead': 'Deleting this profile deletes it for everyone in it, on every device. It cannot be undone.',
  'cloud.deleteOrg': 'Delete “{name}”',
  'cloud.deleteConfirm': 'Delete “{name}” for everyone in it? This cannot be undone.',
  'ledgers.title': 'Profile',
  'ledgers.name': 'Name',
  'ledgers.create': 'Create',
  'ledgers.new': 'New profile on this device',
  'ledgers.delete': 'Delete this profile from this device',
  'ledgers.deleteConfirm': 'Delete “{name}” from this device? It is not anywhere else, and this cannot be undone.',
  'filter.files': 'Files',
  'filter.attached': 'with files',
  'filter.unattached': 'without files',
  'attach.title': 'Files',
  'attach.add': 'Attach a file',
  'attach.photo': 'Take a photo',
  'attach.drop': 'Images and PDFs, up to 20 MB — drop or paste them here too.',
  'attach.open': 'Open {name}',
  'attach.remove': 'Remove {name}',
  'attach.missing': 'not on this device',
  'attach.later': 'Files can be attached to a profile on this device that is not encrypted, for now.',
  'attach.count': {
    one: '{count} file',
    other: '{count} files',
  },
  'error.attachment-type': 'Only images and PDFs can be attached.',
  'error.attachment-too-big': 'That file is over 20 MB.',
  'error.attachment-missing': 'That file is not on this device.',
  'error.attachment-damaged': 'That file is damaged: it is not what was attached.',
  'settings.filesUsed': 'Attached files take {used}.',
  'settings.filesUsedOf': 'Attached files take {used} of {limit}.',
  'settings.storage.sealed': 'Kept encrypted in this browser\'s storage; it opens only with the passphrase.',
  'attach.none': 'No files attached.',
  'error.files-full': 'This profile\'s files are full: remove some to attach more.',
  'attach.pending': 'still on its way',
  'attach.offline': 'needs a connection',
  'attach.queued': 'waiting to upload',
  'error.attachment-pending': 'This file is still on its way from the device that attached it.',
  'error.attachment-offline': 'This file is not on this device yet, and the server cannot be reached.',
  'sync.outbox': {
    one: '{count} attached file waiting to upload',
    other: '{count} attached files waiting to upload',
  },
  'sync.outboxFailed': 'They could not go up: {reason}',
  'states.files': 'Files: {done} of {total}',
  'backup.filesMissing': {
    one: 'Backed up — but {count} attached file is not on this device and could not go in.',
    other: 'Backed up — but {count} attached files are not on this device and could not go in.',
  },
  'settings.restoredMissing': {
    one: 'Restored — but {count} attached file was not in the backup.',
    other: 'Restored — but {count} attached files were not in the backup.',
  },
  'error.zip.no-ledger': 'There is no financial data in that backup.',
  'error.zip.locked': 'That backup is protected by a password.',
}
